import { useCallback } from 'react';
import type { DataGroup } from '@/features/DataGrid';
import { useMetricsStabilizedValue } from '@/shared/hooks';
import { useAppDispatch } from '@/shared/model';
import { getPage, getTotalData, getTreeData, getMetricsMinMax } from './slice';
import type { DataConfig } from './types';

export function useDataFetchers(config: DataConfig) {
  const dispatch = useAppDispatch();
  const stableMetrics = useMetricsStabilizedValue(config.metrics);

  const onGetRows = useCallback(
    (offset?: number) => {
      return dispatch(
        getPage({
          dimensions: config.dimensions,
          metrics: stableMetrics,
          dateScale: config.dateScale,
          dateRange: config.dateRange,
          sorting: config.sorting,
          probabilisticAttribution: config.probabilisticAttribution,
          lifetime: config.lifetime,
          filters: config.filters,
          offset,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      config.dimensions,
      stableMetrics,
      config.dateScale,
      config.dateRange,
      config.sorting,
      config.probabilisticAttribution,
      config.lifetime,
      config.filters,
    ],
  );

  const onGetTotal = useCallback(
    () => {
      return dispatch(
        getTotalData({
          dimensions: config.dimensions,
          metrics: stableMetrics,
          dateRange: config.dateRange,
          lifetime: config.lifetime,
          filters: config.filters,
          probabilisticAttribution: config.probabilisticAttribution,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      config.dimensions,
      stableMetrics,
      config.dateRange,
      config.lifetime,
      config.filters,
      config.probabilisticAttribution,
    ],
  );

  const onGetTreeData = useCallback(
    (dataGroups: DataGroup[]) => {
      return dispatch(
        getTreeData({
          dimensions: config.dimensions,
          metrics: stableMetrics,
          dateScale: config.dateScale,
          dateRange: config.dateRange,
          sorting: config.sorting,
          probabilisticAttribution: config.probabilisticAttribution,
          lifetime: config.lifetime,
          filters: config.filters,
          parentGroups: dataGroups,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      config.dimensions,
      stableMetrics,
      config.dateScale,
      config.dateRange,
      config.sorting,
      config.probabilisticAttribution,
      config.lifetime,
      config.filters,
    ],
  );

  const onGetMinMax = useCallback(
    () => {
      return dispatch(
        getMetricsMinMax({
          dimensions: config.dimensions,
          metrics: stableMetrics,
          filters: config.filters,
          dateRange: config.dateRange,
          dateScale: config.dateScale,
          probabilisticAttribution: config.probabilisticAttribution,
          lifetime: config.lifetime,
          dataGridViewType: config.dataGridViewType,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      config.dataGridViewType,
      config.dimensions,
      stableMetrics,
      config.filters,
      config.dateRange,
      config.dateScale,
      config.probabilisticAttribution,
      config.lifetime,
    ],
  );

  return { onGetRows, onGetTotal, onGetTreeData, onGetMinMax };
}
